/**
 * The worklists the provider workspace offers, and the building-wide resident search (2026-10-21).
 *
 * Provider Cindy Aragon: "is it possible to be missing a patient? I screened someone on the old manifest and he is
 * not showing up on the new manifest when I look for him." Two causes, both fixed here and on the server
 * (daySheetService.js):
 *   1. a resident screened DNQ whose annual is not due is off Current manifest by design, and was reachable only
 *      under Census -- now also under its own worklist, Screened DNQ (exactly Census minus Current manifest);
 *   2. the search box searched only the loaded page (50 residents) -- it still filters the page instantly, and now
 *      also asks the server about the whole building and says "Found in <worklist>: show it".
 *
 * The server owns every predicate. This list adds Screened DNQ to providerApi's ROSTER_LENSES (which this change
 * does not edit; when providerApi.ts next changes, fold SCREENED_DNQ into ROSTER_LENSES and RosterLens there).
 */
import { api } from "../../../services/api";
import { ROSTER_LENSES, RosterLens, RosterOrder } from "../providerApi";

export type WorklistLens = RosterLens | "screened-dnq";

export interface WorklistLensInfo {
  key: WorklistLens;
  label: string;
  primary: boolean;
  what: string;
}

export const SCREENED_DNQ: WorklistLensInfo = {
  key: "screened-dnq",
  label: "Screened DNQ",
  primary: false,
  what: "Census residents screened DNQ whose annual is not yet due: the ones the Current manifest leaves out. Here so a resident you screened can always be found.",
};

/** The server's order: Current manifest, Census, Screened DNQ, then the rest. */
export const WORKLIST_LENSES: ReadonlyArray<WorklistLensInfo> = [
  ...ROSTER_LENSES.slice(0, 2),
  SCREENED_DNQ,
  ...ROSTER_LENSES.slice(2),
];

export const isWorklistLens = (v: string | null | undefined): v is WorklistLens =>
  WORKLIST_LENSES.some((l) => l.key === v);

export const worklistLabel = (k: string): string =>
  WORKLIST_LENSES.find((l) => l.key === k)?.label || k;

/** One resident found anywhere in the building (summary tier: name, room, status). */
export interface BuildingMatch {
  id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  status: string | null;
  lenses: WorklistLens[];
  in_lens: boolean;
  /** Where to show them: the worklist and the page offset there; null when no worklist holds their status. */
  show: { lens: WorklistLens; offset: number; position: number } | null;
}

export interface BuildingSearch {
  query: string;
  lens: WorklistLens;
  order: RosterOrder;
  page_size: number;
  matches: BuildingMatch[];
  more: boolean;
}

export interface SearchResult {
  success: boolean;
  data?: BuildingSearch;
  error?: string;
  code?: string;
  message?: string;
  status?: number;
}

export const residentSearchApi = {
  search: async (
    facilityId: string,
    q: string,
    params: { lens: string; order: RosterOrder; limit: number },
  ): Promise<SearchResult> => {
    const qp = new URLSearchParams({ q, lens: params.lens, order: params.order, limit: String(params.limit) });
    return (await api.get(
      `/emerge/day-sheet/${encodeURIComponent(facilityId)}/residents/search?${qp.toString()}`,
    )) as SearchResult;
  },
};
